import clsx from 'clsx';
import { forwardRef } from 'react';

import { blockquoteCSS } from './Blockquote.sty.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';

const COMPONENT_NAME = 'Blockquote';

/**
 * The props for the Blockquote component.
 * @public
 */
export interface BlockquoteProps
  extends WithChildren,
    AriaLabelingProps,
    StylingProps,
    DataTestId,
    MaskingProps,
    BehaviorTrackingProps {
  /**
   * A URL that designates a source document or message for the information quoted.
   * This attribute is intended to point to information explaining the context for the quoted content.
   */
  cite?: string;
}

/**
 * The `Blockquote` component wraps longer text blocks and indicates that the passage is quoted from another source.
 * @public
 */
export const Blockquote = /* @__PURE__ */ forwardRef<
  HTMLQuoteElement,
  BlockquoteProps
>((props, ref) => {
  const {
    children,
    cite,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  } = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);
  const ariaLabelingProps = useAriaLabelingProps(restProps);
  const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

  return (
    <blockquote
      cite={cite}
      ref={ref}
      className={clsx(blockquoteCSS, consumerClassName)}
      style={consumerStyle}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      {...ariaLabelingProps}
      {...behavioralTrackingProps}
      {...forwardProps}
      data-dt-component={COMPONENT_NAME}
    >
      {children}
    </blockquote>
  );
});

(Blockquote as typeof Blockquote & { displayName: string }).displayName =
  COMPONENT_NAME;
